import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useFavorites } from "@/hooks/useFavorites";
import { CATEGORY_META, getRelated, getTitleById } from "@/lib/media";
import SectionHeading from "@/components/base/SectionHeading";
import TitleGrid from "@/components/base/TitleGrid";
import EmptyState from "@/components/base/EmptyState";
import PlayerDemo from "./components/PlayerDemo";

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const item = id ? getTitleById(id) : undefined;
  const { isFavorite, toggleFavorite } = useFavorites();
  const [fav, setFav] = useState(false);
  const [toast, setToast] = useState("");
  const playerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (item) setFav(isFavorite(item.id));
  }, [item, isFavorite]);

  usePageMeta({
    title: item ? `${item.title}高清在线观看 - 西瓜影音` : "影视详情 - 西瓜影音",
    description: item
      ? `《${item.title}》${item.year}年${item.region}${CATEGORY_META[item.type].label}，由${item.director}执导，${item.actors.join("、")}主演。${item.synopsis}`
      : "西瓜影音影视内容详情页。",
    keywords: item ? `${item.title},${item.title}在线观看,西瓜影音,${item.genres.join(",")}` : "西瓜影音",
  });

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 2200);
  };

  const handleFavorite = () => {
    if (!item) return;
    const next = toggleFavorite(item.id);
    setFav(next);
    showToast(next ? "已加入我的收藏" : "已取消收藏");
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        showToast("链接已复制到剪贴板");
      } else {
        showToast("请手动复制浏览器地址栏链接");
      }
    } catch {
      showToast("复制失败，请手动复制地址栏链接");
    }
  };

  const scrollToPlayer = () => {
    playerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (!item) {
    return (
      <div className="w-full px-4 md:px-8 py-16 md:py-24">
        <EmptyState
          icon="ri-file-unknow-line"
          title="没有找到这部影视作品"
          description="它可能已经下线，或者链接有误。返回首页看看其他精彩内容吧。"
        >
          <Link
            to="/"
            className="mt-6 px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
          >
            返回首页
          </Link>
        </EmptyState>
      </div>
    );
  }

  const meta = CATEGORY_META[item.type];
  const related = getRelated(item, 6);
  const infoRows = [
    { label: "导演", value: item.director },
    { label: "主演", value: item.actors.join(" / ") },
    { label: "年份", value: `${item.year} 年` },
    { label: "地区", value: item.region },
    { label: item.type === "tv" ? "集数" : "时长", value: item.duration },
    { label: "评分", value: `${item.rating.toFixed(1)} 分` },
  ];

  return (
    <div className="animate-fade-in">
      {/* 顶部横幅 */}
      <section className="relative w-full h-[360px] md:h-[460px] overflow-hidden">
        <img
          src={item.banner}
          alt={`${item.title} 背景海报`}
          title={`${item.title} 高清在线观看`}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/70 to-background-950/40"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-background-950/90 via-background-950/30 to-transparent"></div>

        <div className="relative z-10 h-full w-full px-4 md:px-8 flex flex-col justify-end pb-8 md:pb-10">
          <nav className="flex flex-wrap items-center gap-2 text-sm text-foreground-400 mb-4" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-400 transition-colors">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to={`/category/${item.type}`} className="hover:text-primary-400 transition-colors">
              {meta.label}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-200 line-clamp-1">{item.title}</span>
          </nav>

          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="px-2.5 py-1 rounded-md bg-primary-500 text-background-50 text-xs font-semibold">
              {meta.label}
            </span>
            <span className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-background-950/70 border border-background-700 text-accent-400 text-xs font-semibold">
              <i className="ri-star-fill"></i>
              {item.rating.toFixed(1)}
            </span>
            <span className="px-2.5 py-1 rounded-md bg-background-950/70 border border-background-700 text-foreground-200 text-xs">
              {item.year}
            </span>
            <span className="px-2.5 py-1 rounded-md bg-background-950/70 border border-background-700 text-foreground-200 text-xs">
              {item.region}
            </span>
          </div>

          <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-50 mb-3 leading-tight">
            {item.title}
          </h1>
          <p className="text-sm md:text-base text-foreground-300 max-w-3xl mb-6 line-clamp-2">
            {item.synopsis}
          </p>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={scrollToPlayer}
              className="flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors"
            >
              <i className="ri-play-fill text-lg"></i>
              立即播放
            </button>
            <button
              type="button"
              onClick={handleFavorite}
              className={`flex items-center gap-2 px-5 py-3 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer border transition-colors ${
                fav
                  ? "bg-accent-500 border-accent-500 text-background-950"
                  : "bg-background-950/60 border-background-600 text-foreground-100 hover:border-accent-500"
              }`}
            >
              <i className={fav ? "ri-heart-fill" : "ri-heart-line"}></i>
              {fav ? "已收藏" : "收藏"}
            </button>
            <button
              type="button"
              onClick={handleShare}
              className="flex items-center gap-2 px-5 py-3 rounded-md bg-background-950/60 border border-background-600 hover:border-primary-500 text-foreground-100 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
            >
              <i className="ri-share-forward-line"></i>
              分享
            </button>
          </div>
        </div>
      </section>

      {/* 主体内容 */}
      <div className="w-full px-4 md:px-8 py-8 md:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          <div className="lg:col-span-2 space-y-8">
            <div ref={playerRef} id="player">
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-50 mb-4 flex items-center gap-2.5">
                <span className="w-1 h-6 bg-primary-500 rounded-full"></span>
                在线播放
              </h2>
              <PlayerDemo item={item} />
            </div>

            <section>
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-50 mb-4 flex items-center gap-2.5">
                <span className="w-1 h-6 bg-primary-500 rounded-full"></span>
                剧情介绍
              </h2>
              <p className="text-sm md:text-base text-foreground-300 leading-relaxed">{item.plot}</p>
            </section>
          </div>

          <aside className="space-y-5">
            <div className="rounded-lg overflow-hidden bg-background-900 border border-background-800">
              <div className="w-full aspect-[2/3] bg-background-800">
                <img
                  src={item.cover}
                  alt={`${item.title} 高清封面`}
                  title={`${item.title} 高清在线观看`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="rounded-lg bg-background-900 border border-background-800 p-5">
              <h3 className="font-heading text-lg font-semibold text-foreground-100 mb-4">基本信息</h3>
              <dl className="space-y-3">
                {infoRows.map((row) => (
                  <div key={row.label} className="flex items-start gap-3 text-sm">
                    <dt className="text-foreground-500 w-12 shrink-0">{row.label}</dt>
                    <dd className="text-foreground-200 flex-1 min-w-0">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="rounded-lg bg-background-900 border border-background-800 p-5">
              <h3 className="font-heading text-lg font-semibold text-foreground-100 mb-4">分类标签</h3>
              <div className="flex flex-wrap gap-2">
                {item.genres.map((genre) => (
                  <Link
                    key={genre}
                    to={`/category/${item.type}?genre=${encodeURIComponent(genre)}`}
                    className="px-3 py-1.5 rounded-full bg-secondary-100/10 border border-background-700 text-foreground-200 text-xs hover:border-primary-500 hover:text-primary-400 transition-colors"
                  >
                    {genre}
                  </Link>
                ))}
                {item.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1.5 rounded-full bg-accent-100/10 border border-background-700 text-foreground-300 text-xs"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </aside>
        </div>

        <section className="mt-12">
          <SectionHeading title="相关推荐" subtitle="根据类型与地区为你精选" />
          <TitleGrid items={related} />
        </section>
      </div>

      {toast && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-full bg-background-800 border border-background-700 text-foreground-100 text-sm animate-fade-up">
          {toast}
        </div>
      )}
    </div>
  );
}